import React, { useState } from 'react';
import { Modal, Table, Button, Space, Form, Input, Popconfirm, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CarOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface Car {
  id: string;
  licensePlate: string;
  make: string;
  model: string;
  color: string;
  nickname?: string;
  isActive: boolean;
}

interface CarsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

const CarsModal: React.FC<CarsModalProps> = ({ open, onClose, userId, userName }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [editingCar, setEditingCar] = useState<Car | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['user-cars', userId],
    queryFn: async () => {
      const response = await api.get(`/admin/users/${userId}/cars`);
      return response.data;
    },
    enabled: !!userId && open,
  });

  const createMutation = useMutation({
    mutationFn: (values: Partial<Car>) => api.post(`/admin/users/${userId}/cars`, values),
    onSuccess: () => {
      message.success(t('cars.added'));
      queryClient.invalidateQueries({ queryKey: ['user-cars', userId] });
      setIsEditing(false);
      form.resetFields();
    },
    onError: (error: any) => message.error(error?.response?.data?.error || t('cars.addError')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ carId, values }: { carId: string; values: Partial<Car> }) => 
      api.put(`/admin/cars/${carId}`, values),
    onSuccess: () => {
      message.success(t('cars.updated'));
      queryClient.invalidateQueries({ queryKey: ['user-cars', userId] });
      setIsEditing(false);
      setEditingCar(null);
      form.resetFields();
    },
    onError: (error: any) => message.error(error?.response?.data?.error || t('cars.updateError')),
  });

  const deleteMutation = useMutation({
    mutationFn: (carId: string) => api.delete(`/admin/cars/${carId}`),
    onSuccess: () => {
      message.success(t('cars.deleted'));
      queryClient.invalidateQueries({ queryKey: ['user-cars', userId] });
    },
    onError: (error: any) => message.error(error?.response?.data?.error || t('cars.deleteError')),
  });

  const handleAdd = () => {
    setEditingCar(null);
    form.resetFields();
    setIsEditing(true);
  };

  const handleEdit = (car: Car) => {
    setEditingCar(car);
    form.setFieldsValue(car);
    setIsEditing(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      if (editingCar) {
        updateMutation.mutate({ carId: editingCar.id, values });
      } else {
        createMutation.mutate(values);
      }
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingCar(null);
    form.resetFields();
  };

  const columns = [
    { title: t('cars.licensePlate'), dataIndex: 'licensePlate', key: 'licensePlate' },
    { title: t('cars.make'), dataIndex: 'make', key: 'make' },
    { title: t('cars.model'), dataIndex: 'model', key: 'model' },
    { title: t('cars.color'), dataIndex: 'color', key: 'color' },
    {
      title: t('cars.nickname'),
      dataIndex: 'nickname',
      key: 'nickname',
      render: (nickname: string) => nickname || '-',
    },
    {
      title: t('cars.active'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => isActive ? t('common.yes') : t('common.no'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: any, record: Car) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Popconfirm title={t('cars.deleteConfirm')} onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={<><CarOutlined /> {t('cars.userCars', { name: userName })}</>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      {!isEditing ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              {t('cars.addCar')}
            </Button>
          </div>
          <Table
            dataSource={data?.cars || []}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            size="small"
/>
        </>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item name="licensePlate" label={t('cars.licensePlate')} rules={[{ required: true }]}>
            <Input placeholder="np. WA 12345" />
          </Form.Item>
          <Form.Item name="make" label={t('cars.make')} rules={[{ required: true }]}>
            <Input placeholder="np. BMW" />
          </Form.Item>
          <Form.Item name="model" label={t('cars.model')} rules={[{ required: true }]}>
            <Input placeholder="np. X5" />
          </Form.Item>
          <Form.Item name="color" label={t('cars.color')} rules={[{ required: true }]}>
            <Input placeholder="np. Czarny" />
          </Form.Item>
          <Form.Item name="nickname" label={t('cars.nicknameOptional')}>
            <Input placeholder={t('cars.placeholderExample')} />
          </Form.Item>
          <Space>
            <Button type="primary" onClick={handleSave} loading={createMutation.isPending || updateMutation.isPending}>
              {editingCar ? t('common.save') : t('common.add')}
            </Button>
            <Button onClick={handleCancel}>{t('common.cancel')}</Button>
          </Space>
        </Form>
      )}
    </Modal>
  );
};

export default CarsModal;